import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft } from 'lucide-react';

const Datenschutz = () => {
  const { language, changeLanguage, availableLanguages } = useLanguage();

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link
              to="/"
              className="text-2xl font-light tracking-[0.3em] text-white hover:text-[#B8860B] transition-colors"
              data-testid="datenschutz-logo"
            >
              ALKA
            </Link>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1 text-xs">
                {availableLanguages.map((lang, idx) => (
                  <React.Fragment key={lang}>
                    <button
                      onClick={() => changeLanguage(lang)}
                      className={`px-2 py-1 transition-colors ${
                        language === lang ? 'text-[#B8860B]' : 'text-white/60 hover:text-white'
                      }`}
                    >
                      {lang.toUpperCase()}
                    </button>
                    {idx < availableLanguages.length - 1 && <span className="text-white/20">|</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-[#A1A1AA] hover:text-[#B8860B] text-sm mb-8 transition-colors"
            data-testid="datenschutz-back"
          >
            <ArrowLeft size={16} />
            <span>{language === 'de' ? 'Zurück zur Startseite' : 'Back to Home'}</span>
          </Link>

          <h1 className="font-outfit text-4xl sm:text-5xl font-light text-white tracking-tight mb-12">
            Datenschutzerklärung
          </h1>

          <div className="prose prose-invert prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">1. Datenschutz auf einen Blick</h2>
              <h3 className="text-white text-lg font-medium mb-2">Allgemeine Hinweise</h3>
              <p className="text-[#A1A1AA] leading-relaxed">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
              </p>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">2. Hosting</h2>
              <p className="text-[#A1A1AA] leading-relaxed">
                Wir hosten die Inhalte unserer Website bei folgenden Anbietern: [Hosting-Anbieter eintragen]
              </p>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">3. Allgemeine Hinweise und Pflichtinformationen</h2>
              <h3 className="text-white text-lg font-medium mb-2">Datenschutz</h3>
              <p className="text-[#A1A1AA] leading-relaxed">
                Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.
              </p>
            </section>

            <section>
              <h3 className="text-white text-lg font-medium mb-2">Hinweis zur verantwortlichen Stelle</h3>
              <p className="text-[#A1A1AA] leading-relaxed mb-4">
                Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
              </p>
              <div className="text-[#A1A1AA] space-y-2">
                <p>[Firmenname / Name]</p>
                <p>[Straße und Hausnummer]</p>
                <p>[PLZ und Ort]</p>
                <p>Telefon: +49 178 5785105</p>
                <p>E-Mail: info@alka.website</p>
              </div>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">4. Datenerfassung auf dieser Website</h2>
              <h3 className="text-white text-lg font-medium mb-2">Kontaktformular</h3>
              <p className="text-[#A1A1AA] leading-relaxed">
                Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von Anschlussfragen bei uns gespeichert. Diese Daten geben wir nicht ohne Ihre Einwilligung weiter.
              </p>
            </section>

            <section>
              <h3 className="text-white text-lg font-medium mb-2">Anfrage per E-Mail oder WhatsApp</h3>
              <p className="text-[#A1A1AA] leading-relaxed">
                Wenn Sie uns per E-Mail oder WhatsApp kontaktieren, wird Ihre Anfrage inklusive aller daraus hervorgehenden personenbezogenen Daten (Name, Anfrage) zum Zwecke der Bearbeitung Ihres Anliegens bei uns gespeichert und verarbeitet.
              </p>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">5. Speicherdauer</h2>
              <p className="text-[#A1A1AA] leading-relaxed">
                Die von Ihnen eingegebenen Daten werden bei uns so lange gespeichert, bis Sie uns zur Löschung auffordern, Ihre Einwilligung zur Speicherung widerrufen oder der Zweck für die Datenspeicherung entfällt. Zwingende gesetzliche Bestimmungen – insbesondere Aufbewahrungsfristen – bleiben unberührt.
              </p>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">6. Ihre Rechte</h2>
              <p className="text-[#A1A1AA] leading-relaxed">
                Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese Einwilligung jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen.
              </p>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">7. SSL- bzw. TLS-Verschlüsselung</h2>
              <p className="text-[#A1A1AA] leading-relaxed">
                Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte, wie zum Beispiel Bestellungen oder Anfragen, die Sie an uns als Seitenbetreiber senden, eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.
              </p>
            </section>

            <section>
              <h2 className="text-[#B8860B] text-xl font-medium mb-4">8. Widerspruch gegen Werbe-E-Mails</h2>
              <p className="text-[#A1A1AA] leading-relaxed">
                Der Nutzung von im Rahmen der Impressumspflicht veröffentlichten Kontaktdaten zur Übersendung von nicht ausdrücklich angeforderter Werbung und Informationsmaterialien wird hiermit widersprochen. Die Betreiber der Seiten behalten sich ausdrücklich rechtliche Schritte im Falle der unverlangten Zusendung von Werbeinformationen, etwa durch Spam-E-Mails, vor.
              </p>
            </section>

            <section className="pt-8 border-t border-white/10">
              <p className="text-[#A1A1AA]/60 text-sm">
                Stand: {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
          <p className="text-[#A1A1AA]/60 text-xs">© {new Date().getFullYear()} ALKA</p>
          <div className="flex items-center space-x-4 text-xs">
            <Link to="/impressum" className="text-[#A1A1AA] hover:text-[#B8860B] transition-colors">Impressum</Link>
            <Link to="/datenschutz" className="text-[#B8860B]">Datenschutz</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Datenschutz;
